

export interface LayoutOptions {
	initialTemperature: number,
	minTemperature: number,
	coolingFactor: number,
	maxIterations: number,
	minDistance: number,
}

const defaultLayoutOptions = {
	initialTemperature: 100,
	minTemperature: 0.1,
	coolingFactor: 0.95,
	maxIterations: 500,
	minDistance: 250,
}

export interface Node {
	id: string,
	x: number,
	y: number,
	w: number,
	h: number,
	fixed?: boolean,
}

export interface Edge {
	source: string,
	target: string,

	routingPoints: any[],
}

export interface Graph {
	nodes: Node[],
	edges: Edge[],

	width: number,
	height: number,
}

export function layoutNodes(graph: Graph, options?: LayoutOptions) {
    if (!options) options = defaultLayoutOptions;

    function initPositions() {
        for (let n of graph.nodes) {
            if (n.fixed) continue;
            // Fix: Assume (x, y) are centers; initialize within bounds accounting for size
            n.x = n.w / 2 + Math.random() * (graph.width - n.w);
            // n.y = n.h / 2 + Math.random() * (graph.height - n.h);
        }
    }

    function calcDisplacement(n1: Node, n2: Node, isAttraction: boolean = false) {
        const k = 0.1; // Spring constant (consider adding to options for tuning)
        let dx = n2.x - n1.x;
        let dy = n2.y - n1.y;
        let distance = Math.sqrt(dx * dx + dy * dy);
        // Fix: Handle near-zero distance with random perturbation to avoid stuck nodes
        if (distance < 0.001) {
            const angle = Math.random() * 2 * Math.PI;
            dx = Math.cos(angle) * 0.001;
            dy = Math.sin(angle) * 0.001;
            distance = 0.001;
        }
        const unitX = dx / distance;
        const unitY = dy / distance;
        if (isAttraction) {
            // Attraction for edges: force proportional to distance
            const force = k * distance;
            return { dx: unitX * force, dy: unitY * force };
        } else {
            // Repulsion for all pairs: force inversely proportional to distance squared
            const force = k / (distance * distance);
            return { dx: -unitX * force, dy: -unitY * force };
        }
    }

    function updatePosition(n: Node, displacement: { dx: number; dy: number }) {
        n.x += displacement.dx;
        n.y += displacement.dy;
    }

    function applyBoundaryConstraints() {
        for (let n of graph.nodes) {
            // Fix: Boundaries for centers
            n.x = Math.max(n.w / 2, Math.min(n.x, graph.width - n.w / 2));
            n.y = Math.max(n.h / 2, Math.min(n.y, graph.height - n.h / 2));
        }
    }

    // New: Explicitly resolve rectangle overlaps (hard constraint)
    function resolveOverlaps() {
        for (let i = 0; i < graph.nodes.length; i++) {
            for (let j = i + 1; j < graph.nodes.length; j++) {
                const n1 = graph.nodes[i];
                const n2 = graph.nodes[j];
                const left1 = n1.x - n1.w / 2;
                const right1 = n1.x + n1.w / 2;
                const top1 = n1.y - n1.h / 2;
                const bottom1 = n1.y + n1.h / 2;
                const left2 = n2.x - n2.w / 2;
                const right2 = n2.x + n2.w / 2;
                const top2 = n2.y - n2.h / 2;
                const bottom2 = n2.y + n2.h / 2;
                if (left1 < right2 && right1 > left2 && top1 < bottom2 && bottom1 > top2) {
                    // Overlap detected; compute minimal separation
                    const overlapX = Math.min(right1 - left2, right2 - left1);
                    const overlapY = Math.min(bottom1 - top2, bottom2 - top1);
                    let sepX = 0;
                    let sepY = 0;
                    if (overlapX < overlapY) {
                        // Separate horizontally
                        sepX = overlapX / 2 + 1; // Small buffer
                    } else {
                        // Separate vertically
                        sepY = overlapY / 2 + 1;
                    }
                    const dirX = n1.x < n2.x ? -sepX : sepX;
                    const dirY = n1.y < n2.y ? -sepY : sepY;
                    if (!n1.fixed) {
                        n1.x += dirX;
                        n1.y += dirY;
                    }
                    if (!n2.fixed) {
                        n2.x -= dirX; // Opposite direction
                        n2.y -= dirY;
                    }
                }
            }
        }
    }

    function updateEdgeRouting() {
        graph.edges.forEach(edge => {
            const node1 = graph.nodes.find(node => node.id === edge.source);
            const node2 = graph.nodes.find(node => node.id === edge.target);
            if (node1 && node2) {
                edge.routingPoints = orthogonalRouting(node1, node2);
            }
        });
    }

    function orthogonalRouting(node1: Node, node2: Node) {
        const points = [];
        const dx = node2.x - node1.x;
        const dy = node2.y - node1.y;
        // Horizontal segment
        if (Math.abs(dx) >= Math.abs(dy)) {
            const midX = (node1.x + node2.x) / 2;
            points.push({ x: midX, y: node1.y });
            points.push({ x: midX, y: node2.y });
        }
        // Vertical segment
        else {
            const midY = (node1.y + node2.y) / 2;
            points.push({ x: node1.x, y: midY });
            points.push({ x: node2.x, y: midY });
        }
        return points;
    }

    initPositions();
    let temperature = options.initialTemperature;

    for (let i = 0; i < options.maxIterations && temperature > options.minTemperature; i++) {
        // Repulsive forces (accumulate for each node)
        for (let n1 of graph.nodes) {
            if (n1.fixed) continue;
            let totalDisplacement = { dx: 0, dy: 0 };
            for (let n2 of graph.nodes) {
                if (n1 === n2) continue;
                const displacement = calcDisplacement(n1, n2, false);
                totalDisplacement.dx += displacement.dx;
                totalDisplacement.dy += displacement.dy;
            }
            updatePosition(n1, totalDisplacement);
        }

        // Attractive forces along edges
        graph.edges.forEach(edge => {
            const node1 = graph.nodes.find(node => node.id === edge.source);
            const node2 = graph.nodes.find(node => node.id === edge.target);
            if (!node1 || !node2) return;
            const displacement = calcDisplacement(node1, node2, true);
            if (!node1.fixed) {
                updatePosition(node1, displacement);
            }
            if (!node2.fixed) {
                updatePosition(node2, { dx: -displacement.dx, dy: -displacement.dy });
            }
        });

        // Minimum distance enforcement (with zero-distance handling)
        const minDistance = options.minDistance;
        graph.nodes.forEach(node1 => {
            graph.nodes.forEach(node2 => {
                if (node1 === node2) return;
                let dx = node2.x - node1.x;
                let dy = node2.y - node1.y;
                let distance = Math.sqrt(dx * dx + dy * dy);
                if (distance < minDistance) {
                    let displacementX: number, displacementY: number;
                    if (distance < 0.001) {
                        const angle = Math.random() * 2 * Math.PI;
                        displacementX = Math.cos(angle) * (minDistance - distance) / 2;
                        displacementY = Math.sin(angle) * (minDistance - distance) / 2;
                    } else {
                        displacementX = (dx / distance) * (minDistance - distance) / 2;
                        displacementY = (dy / distance) * (minDistance - distance) / 2;
                    }
                    if (!node1.fixed) {
                        updatePosition(node1, { dx: -displacementX, dy: -displacementY });
                    }
                    if (!node2.fixed) {
                        updatePosition(node2, { dx: displacementX, dy: displacementY });
                    }
                }
            });
        });

        // New: Resolve any remaining rectangle overlaps
        resolveOverlaps();

        applyBoundaryConstraints();

        updateEdgeRouting();

        temperature *= options.coolingFactor;
    }
}

// export function layoutNodes(graph: Graph, options?: LayoutOptions) {
//     if (!options) options = defaultLayoutOptions;

// 	function initPositions() {
// 		for(let n of graph.nodes) {
// 			if (n.fixed) continue;
// 			n.x = Math.random() * (graph.width - n.w);
// 			n.y = Math.random() * (graph.height - n.h);
// 		}
// 	}

// 	function calcDisplacement(n1: Node, n2: Node, isEdge: boolean = false) {
// 		const k = 0.1; // Consider moving to LayoutOptions
// 		const dx = n2.x - n1.x;
// 		const dy = n2.y - n1.y;
// 		const distance = Math.sqrt(dx * dx + dy * dy) || 0.0001; // Avoid division by zero

// 		if (isEdge) {
// 			// Attractive force for edges (proportional to distance)
// 			const force = k * distance;
// 			return { dx: (dx / distance) * force, dy: (dy / distance) * force };
// 		} else {
// 			// Repulsive force for node pairs (inversely proportional to distance)
// 			const force = k / (distance * distance); // Fruchterman-Reingold style
// 			return { dx: -(dx / distance) * force, dy: -(dy / distance) * force };
// 		}
// 	}

// 	function updatePosition(n: Node, displacement: {
// 		dx: number,
// 		dy: number,
// 	}) {
// 		n.x += displacement.dx;
// 		n.y += displacement.dy;
// 	}

// 	function applyBoundaryConstraints() {
// 		for(let n of graph.nodes) {
// 			n.x = Math.max(n.w, Math.min(n.x, graph.width - n.w));
// 			n.y = Math.max(n.h, Math.min(n.y, graph.height - n.h));
// 		}
	
// 	}

// 	function updateEdgeRouting() {
// 		graph.edges.forEach(edge => {
// 			const node1 = graph.nodes.find(node => node.id === edge.source);
// 			const node2 = graph.nodes.find(node => node.id === edge.target);
// 			edge.routingPoints = orthogonalRouting(node1!, node2!);
// 		});
// 	}
	
// 	// Compute orthogonal routing for an edge between two nodes
// 	function orthogonalRouting(node1: Node, node2: Node) {
// 		const points = [];
// 		const dx = node2.x - node1.x;
// 		const dy = node2.y - node1.y;
	
// 		// Horizontal segment
// 		if (Math.abs(dx) >= Math.abs(dy)) {
// 			const midX = (node1.x + node2.x) / 2;
// 			points.push({ x: midX, y: node1.y });
// 			points.push({ x: midX, y: node2.y });
// 		}
// 		// Vertical segment
// 		else {
// 			const midY = (node1.y + node2.y) / 2;
// 			points.push({ x: node1.x, y: midY });
// 			points.push({ x: node2.x, y: midY });
// 		}
	
// 		return points;
// 	}	

//     initPositions();
//     let temperature = options.initialTemperature;

// for (let i = 0; i < options.maxIterations && temperature > options.minTemperature; i++) {
//         // Repulsive forces
//         for (let n1 of graph.nodes) {
//             if (n1.fixed) continue;
//             let totalDisplacement = { dx: 0, dy: 0 };
//             for (let n2 of graph.nodes) {
//                 if (n1 !== n2) {
//                     const displacement = calcDisplacement(n1, n2); // From previous example
//                     totalDisplacement.dx += displacement.dx;
//                     totalDisplacement.dy += displacement.dy;
//                 }
//             }
//             updatePosition(n1, totalDisplacement);
//         }

//         // Attractive forces for edges
//         for (let edge of graph.edges) {
//             const node1 = graph.nodes.find(node => node.id === edge.source);
//             const node2 = graph.nodes.find(node => node.id === edge.target);
//             if (!node1 || !node2) continue;
//             const displacement = calcDisplacement(node1, node2, true);
//             if (!node1.fixed) {
//                 updatePosition(node1, displacement);
//             }
//             if (!node2.fixed) {
//                 updatePosition(node2, { dx: -displacement.dx, dy: -displacement.dy });
//             }
//         }

//         // Additional force to avoid edge overlaps
//         for (let edge1 of graph.edges) {
//             const n1 = graph.nodes.find(node => node.id === edge1.source);
//             const n2 = graph.nodes.find(node => node.id === edge1.target);
//             if (!n1 || !n2) continue;

//             for (let edge2 of graph.edges) {
//                 if (edge1 === edge2) continue;
//                 const n3 = graph.nodes.find(node => node.id === edge2.source);
//                 const n4 = graph.nodes.find(node => node.id === edge2.target);
//                 if (!n3 || !n4) continue;

//                 // Check if edges are aligned (same x or y for segments)
//                 const dx1 = n2.x - n1.x;
//                 const dy1 = n2.y - n1.y;
//                 const dx2 = n4.x - n3.x;
//                 const dy2 = n4.y - n3.y;

//                 if (Math.abs(dx1) < options.minDistance / 2 && Math.abs(dx2) < options.minDistance / 2) {
//                     // Both edges are near-vertical, push nodes apart horizontally
//                     const midY1 = (n1.y + n2.y) / 2;
//                     const midY2 = (n3.y + n4.y) / 2;
//                     if (Math.abs(midY1 - midY2) < options.minDistance) {
//                         const displacement = options.minDistance / 4;
//                         if (!n1.fixed) updatePosition(n1, { dx: -displacement, dy: 0 });
//                         if (!n2.fixed) updatePosition(n2, { dx: -displacement, dy: 0 });
//                         if (!n3.fixed) updatePosition(n3, { dx: displacement, dy: 0 });
//                         if (!n4.fixed) updatePosition(n4, { dx: displacement, dy: 0 });
//                     }
//                 } else if (Math.abs(dy1) < options.minDistance / 2 && Math.abs(dy2) < options.minDistance / 2) {
//                     // Both edges are near-horizontal, push nodes apart vertically
//                     const midX1 = (n1.x + n2.x) / 2;
//                     const midX2 = (n3.x + n4.x) / 2;
//                     if (Math.abs(midX1 - midX2) < options.minDistance) {
//                         const displacement = options.minDistance / 4;
//                         if (!n1.fixed) updatePosition(n1, { dx: 0, dy: -displacement });
//                         if (!n2.fixed) updatePosition(n2, { dx: 0, dy: -displacement });
//                         if (!n3.fixed) updatePosition(n3, { dx: 0, dy: displacement });
//                         if (!n4.fixed) updatePosition(n4, { dx: 0, dy: displacement });
//                     }
//                 }
//             }
//         }

//         // Minimum distance enforcement
//         for (let n1 of graph.nodes) {
//             if (n1.fixed) continue;
//             for (let n2 of graph.nodes) {
//                 if (n1 !== n2) {
//                     const dx = n2.x - n1.x;
//                     const dy = n2.y - n1.y;
//                     const distance = Math.sqrt(dx * dx + dy * dy) || 0.0001;
//                     if (distance < options.minDistance) {
//                         const displacementX = (dx / distance) * (options.minDistance - distance) / 2;
//                         const displacementY = (dy / distance) * (options.minDistance - distance) / 2;
//                         if (!n1.fixed) {
//                             updatePosition(n1, { dx: -displacementX, dy: -displacementY });
//                         }
//                         if (!n2.fixed) {
//                             updatePosition(n2, { dx: displacementX, dy: displacementY });
//                         }
//                     }
//                 }
//             }
//         }

//         applyBoundaryConstraints();
//         temperature *= options.coolingFactor;
//     }

//     updateEdgeRouting(); // Move routing to the end for efficiency
// }

